import {toPriceStr} from '@/utils';
import {BalanceListItem, PayMethod} from '../recharge.service';
import {ActivityPay, BalanceConfig} from '../recharge-tab/recharge-tab.service';

export const EXCLUDE_TYPE = ['RECHARGE_CUMULATE_AMOUNT'];

/**
 * 奖励计算结果接口
 */
export interface BonusCalculationResult {
  payMethodResult: number; // 支付方式奖励
  tabBonusResult: number; // Tab bonus奖励
  payBalanceResult: number; // 余额项奖励
  totalResult: number; // 总奖励
}

/**
 * 计算充值奖励总额（包含tab bonus）
 * @param balance 充值金额
 * @param payMethod 支付方式
 * @param balanceItem 余额项
 * @param activeTab 当前选中的tab
 * @param isDynamicFixed 是否动态固定
 * @returns 格式化的奖励字符串
 */
export function getTotalSentPriceStrWithTabBonus(
  balance: string,
  payMethod?: PayMethod,
  balanceItem?: BalanceListItem,
  activeTab?: ActivityPay,
  isDynamicFixed?: boolean,
) {
  // 直接从calculateTotalBonus中获取结果
  const result = calculateTotalBonus(
    balance,
    payMethod,
    balanceItem,
    activeTab,
  );
  return result.totalResult <= 0
    ? ''
    : '+' +
        toPriceStr(result.totalResult, {
          fixed: isDynamicFixed ? 0 : 2,
          maxFixed: 2,
        });
}

/**
 * 计算总奖励金额（包含tab bonus）- 返回详细结果
 * @param balance 充值金额
 * @param payMethod 支付方式
 * @param balanceItem 余额项
 * @param activeTab 当前选中的tab
 * @param includeTabBonus 是否包含tab bonus
 * @returns 奖励计算结果map
 */
export function calculateTotalBonus(
  balance: string,
  payMethod?: PayMethod,
  balanceItem?: BalanceListItem,
  activeTab?: ActivityPay,
): BonusCalculationResult {
  if (!balance || isNaN(Number(balance)) || Number(balance) <= 0) {
    return {
      payMethodResult: 0,
      tabBonusResult: 0,
      payBalanceResult: 0,
      totalResult: 0,
    };
  }

  const balanceNum = Number(balance);

  // 计算基础奖励（giveScale * balance）
  const payMethodResult =
    payMethod?.giveWay != null
      ? payMethod.giveWay === 1
        ? payMethod.giveFixedAmount || 0
        : payMethod.giveWay === 0
        ? ((payMethod.ratio || 0) * balanceNum) / 100
        : 0
      : 0;

  // 计算tab bonus奖励
  let tabBonusResult = 0;
  // 计算逻辑需要变更下，他是按照当前金额是否在规则区间，如果在，则用规则的bonus，如果不在，则用0
  // 规则的bonus 是按照金额区间计算的，所以需要先计算出金额区间
  // 金额区间是按照minAmount和maxAmount计算的，所以需要先计算出金额区间
  // 金额区间是按照minAmount和maxAmount计算的，所以需要先计算出金额区间
  if (
    activeTab &&
    activeTab.activityRechargeAwardList &&
    !EXCLUDE_TYPE.includes(activeTab.type)
  ) {
    // 当前balance 在哪个区间内
    const currentBalanceInRange = activeTab.activityRechargeAwardList.find(
      item =>
        balanceNum >= Number(item.minAmount) &&
        balanceNum <= Number(item.maxAmount),
    );
    if (currentBalanceInRange) {
      // valueType PERCENT_VALUE z则是百分比， 其他就是数值
      if (currentBalanceInRange?.valueType !== 'PERCENT_VALUE') {
        tabBonusResult = Number(currentBalanceInRange.value);
      } else {
        tabBonusResult = Number(
          ((Number(currentBalanceInRange.value) * balanceNum) / 100).toFixed(2),
        );
      }
    }
  }

  // 计算balanceItem奖励（原有的ratio逻辑）
  const payBalanceResult = Number(
    (((balanceItem?.ratio || 0) / 100) * balanceNum).toFixed(2) || 0,
  );

  const totalResult = tabBonusResult + payBalanceResult;

  return {
    payMethodResult,
    tabBonusResult,
    payBalanceResult,
    totalResult,
  };
}

/**
 * 检查充值金额是否在tab区间内
 * @param balance 充值金额
 * @param activeTab 当前选中的tab
 * @returns 是否在区间内
 */
export function isBalanceInTabRange(balance: string, activeTab?: ActivityPay) {
  if (!balance || isNaN(Number(balance)) || Number(balance) <= 0) {
    return false;
  }

  const balanceNum = Number(balance);

  if (activeTab && activeTab.minAmount && activeTab.maxAmount) {
    return (
      balanceNum >= activeTab.minAmount && balanceNum <= activeTab.maxAmount
    );
  }

  return false;
}

/**
 * 计算tab bonus金额
 * @param balance 充值金额
 * @param activeTab 当前选中的tab
 * @returns tab bonus金额
 */
export function calculateTabBonus(balance: string, activeTab?: ActivityPay) {
  const isInRange = isBalanceInTabRange(balance, activeTab);

  if (!isInRange || !activeTab?.cornerMarkValue) {
    return 0;
  }

  const balanceNum = Number(balance);
  const bonusStr = activeTab.cornerMarkValue.trim();

  if (bonusStr.includes('%')) {
    // 带%的情况：balance * bonus百分比
    const bonusPercentage = parseFloat(bonusStr.replace('%', ''));
    return Number(((bonusPercentage * balanceNum) / 100).toFixed(2));
  } else {
    // 不带%的情况：balance + bonus固定值
    const bonusValue = parseFloat(bonusStr);
    return balanceNum + bonusValue;
  }
}

/**
 * 计算按钮显示的第一行文本（充值金额）
 * @param balance 充值金额
 * @returns 第一行显示文本
 */
export function getButtonFirstLineText(balance: string): string {
  if (!balance || isNaN(Number(balance)) || Number(balance) <= 0) {
    return 'Recharge';
  }
  return `Recharge ₹${balance}`;
}

/**
 * 计算按钮显示的第二行文本（总奖励金额）
 * @param balance 充值金额
 * @param payMethod 支付方式
 * @param balanceItem 余额项
 * @param activeTab 当前选中的tab
 * @returns 第二行显示文本
 */
export function getButtonSecondLineText(
  balance: string,
  payMethod?: PayMethod,
  balanceItem?: BalanceListItem,
  activeTab?: ActivityPay,
  balanceConfig?: BalanceConfig[],
): string {
  if (!balance || isNaN(Number(balance)) || Number(balance) <= 0) {
    return '';
  }

  const balanceNum = Number(balance);
  const result = calculateTotalBonus(
    balance,
    payMethod,
    balanceItem,
    activeTab,
  );

  // 过滤掉值为0的部分
  const parts: string[] = [];

  // 总是显示充值金额
  parts.push(`₹${balanceNum}`);

  // 只添加非0的奖励值
  if (result.tabBonusResult > 0) {
    parts.push(`₹${result.tabBonusResult}`);
  }

  if (result.payMethodResult > 0) {
    parts.push(`₹${result.payMethodResult}`);
  }
  let balaceResult = 0;

  if (result.payBalanceResult > 0) {
    balaceResult = result.payBalanceResult;
    parts.push(`₹${result.payBalanceResult}`);
  } else {
    if (balanceConfig) {
      // 判断balance 在balanceConfig 的哪个区间内
      const currentBalanceInRange = balanceConfig?.find(
        item =>
          balanceNum >= Number(item.minAmount) &&
          balanceNum <= Number(item.maxAmount),
      );

      if (currentBalanceInRange) {
        const giveScale = balanceNum * (currentBalanceInRange.giveScale / 100);
        parts.push(`${giveScale}`);
        balaceResult = giveScale;
      }
    }
  }
  const total =
    Number(balance) +
    Number(result.tabBonusResult) +
    Number(result.payMethodResult) +
    Number(balaceResult);
  // 如果不是整数，则取2位小数
  if (total % 1 !== 0) {
    return `₹${total.toFixed(2)}`;
  }
  return `₹${total}`;
  // return `(${parts.join('+')})`;
}

/**
 * 计算右上角显示的奖励信息
 * @param activeTab 当前选中的tab
 * @param payMethod 支付方式
 * @param balanceItem 余额项
 * @returns 右上角显示文本
 */
export function getTopRightBonusText(
  activeTab?: ActivityPay,
  payMethod?: PayMethod,
  balanceItem?: BalanceListItem,
  balance?: string,
  balanceConfig?: BalanceConfig[],
): string {
  const parts: string[] = [];
  const balanceNum = Number(balance);
  // 活动奖励
  if (
    activeTab?.activityRechargeAwardList &&
    !EXCLUDE_TYPE.includes(activeTab.type) // 这里type 要不写在公共常量里面
  ) {
    const currentBalanceInRange = activeTab.activityRechargeAwardList.find(
      item =>
        balanceNum >= Number(item.minAmount) &&
        balanceNum <= Number(item.maxAmount),
    );
    if (currentBalanceInRange) {
      if (currentBalanceInRange?.valueType !== 'PERCENT_VALUE') {
        parts.push(`${currentBalanceInRange.value}`);
      } else {
        parts.push(`${currentBalanceInRange.value}%`);
      }
    }
  }
  //  渠道奖励
  // 这里有问题，如果paymethod giveway 是1，展示giveFixedAmount 那么就展示giveScale * 100
  if (payMethod?.giveWay === 0) {
    parts.push(`${payMethod.ratio}%`);
  } else if (payMethod?.giveWay === 1) {
    parts.push(`₹${payMethod.giveFixedAmount}`);
  }
  // 根据store存储的内容，如果balance 有值，判断balance 在balanceList 的哪个区间内，如果存在，则展示balanceList 的ratio

  // balanceItem ratio
  // 重置奖励
  if (balance) {
    // 判断balance 在balanceConfig 的哪个区间内
    const currentBalanceInRange = balanceConfig?.find(
      item =>
        balanceNum >= Number(item.minAmount) &&
        balanceNum <= Number(item.maxAmount),
    );
    if (currentBalanceInRange) {
      parts.push(`${currentBalanceInRange.giveScale}%`);
    }
  } else {
    if (balanceItem?.ratio) {
      parts.push(`${balanceItem.ratio}%`);
    }
  }
  // console.log(parts, '>>>>parts', balanceItem, payMethod, activeTab);

  return parts.join('+');
}
